import { useState, useEffect, useRef, useMemo } from 'react';
import dateUtil from '../../../utils/dateUtil';
import ShiftService from '../services/ShiftService';
import httpService from '../../../core/services/HttpService';
import TenantService from '../../settings/company/services/TenantService';
import EventService from '../../events/services/EventService';
import { UnifiedEvent } from '../../events/types';
import { unifyEvents } from '../../events/services/unifyEvents';

type Absence = {
  id: number;
  employee: number;
  status: string;
  start_datetime: string;
  end_datetime: string;
  absence_type?: string;
};

interface UseShiftEnvironmentDeps {
  activeTab: string;
  startDate: Date;
  draggableWAList: any[];
  hiddenWorkAreas: number[];
  selectedLocations: number[];
  setHiddenWorkAreas: (v: number[]) => void;
}

export function useShiftEnvironment(deps: UseShiftEnvironmentDeps) {
  const {
    activeTab, startDate, draggableWAList, hiddenWorkAreas,
    selectedLocations, setHiddenWorkAreas,
  } = deps;

  const [weatherData, setWeatherData] = useState<Record<string, any>>({});
  const [weatherByLocation, setWeatherByLocation] = useState<Record<number, Record<string, any>>>({});
  const [headquartersLocationId, setHeadquartersLocationId] = useState<number | null>(null);
  const [weekEvents, setWeekEvents] = useState<UnifiedEvent[]>([]);
  const [weekAbsences, setWeekAbsences] = useState<Absence[]>([]);
  const [allAbsencesForSidebar, setAllAbsencesForSidebar] = useState<Absence[]>([]);
  const [countsByDate, setCountsByDate] = useState<Record<string, { red: number; orange: number }>>({});

  const uniqueLocations = useMemo(() => {
    const locMap = new Map<number, string>();
    draggableWAList.forEach((wa: any) => {
      if (wa.location && wa.location_name) {
        locMap.set(wa.location, wa.location_name);
      }
    });
    return Array.from(locMap, ([id, name]) => ({ id, name }));
  }, [draggableWAList]);

  const visibleLocationIds = useMemo(() => {
    const locIds = new Set<number>();
    draggableWAList
      .filter((wa: any) => !hiddenWorkAreas.includes(wa.id))
      .forEach((wa: any) => {
        if (wa.location) locIds.add(wa.location);
      });
    return Array.from(locIds);
  }, [draggableWAList, hiddenWorkAreas]);

  const weatherLocationId = visibleLocationIds.length > 0 ? visibleLocationIds[0] : null;
  const visibleLocationIdsKey = visibleLocationIds.join(',');

  useEffect(() => {
    TenantService.getLocations()
      .then((locations: any) => {
        const hq = locations.find((l: any) => l.is_headquarters);
        if (hq) setHeadquartersLocationId(hq.id);
      })
      .catch(() => {});
  }, []);

  // ===== Location sync =====
  const locationSyncDone = useRef(false);
  useEffect(() => {
    if (draggableWAList.length === 0) return;
    if (selectedLocations.length === 0) {
      if (locationSyncDone.current) setHiddenWorkAreas([]);
      locationSyncDone.current = true;
      return;
    }
    locationSyncDone.current = true;
    const waIdsToHide = draggableWAList
      .filter((wa: any) => wa.location && !selectedLocations.includes(wa.location))
      .map((wa: any) => wa.id);
    setHiddenWorkAreas(waIdsToHide);
  }, [selectedLocations, draggableWAList, setHiddenWorkAreas]);

  // ===== Weather =====
  useEffect(() => {
    if (visibleLocationIds.length === 0) {
      setWeatherData({});
      setWeatherByLocation({});
      return;
    }
    const monday = dateUtil.getMonday(startDate);
    const start = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;

    if (visibleLocationIds.length === 1) {
      ShiftService.getWeather(visibleLocationIds[0], start, 7)
        .then((res: any) => {
          const map: Record<string, any> = {};
          (res.forecast || []).forEach((d: any) => { map[d.date] = d; });
          setWeatherData(map);
          setWeatherByLocation({ [visibleLocationIds[0]]: map });
        })
        .catch(() => { setWeatherData({}); setWeatherByLocation({}); });
    } else {
      ShiftService.getWeatherMultiLocation(visibleLocationIds, start, 7)
        .then((res: any) => {
          const byLoc: Record<number, Record<string, any>> = {};
          (res.locations || []).forEach((loc: any) => {
            const map: Record<string, any> = {};
            (loc.forecast || []).forEach((d: any) => { map[d.date] = d; });
            byLoc[loc.location_id] = map;
          });
          setWeatherByLocation(byLoc);
          const primaryId = weatherLocationId || visibleLocationIds[0];
          setWeatherData(byLoc[primaryId] || {});
        })
        .catch(() => { setWeatherData({}); setWeatherByLocation({}); });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleLocationIdsKey, startDate, weatherLocationId]);

  // ===== Events =====
  useEffect(() => {
    if (!weatherLocationId) { setWeekEvents([]); return; }
    const monday = dateUtil.getMonday(startDate);
    const start = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
    EventService.getEvents(weatherLocationId, start, 7)
      .then((data: any) => {
        console.log('[ShiftEvents] loaded', data?.holidays?.length, 'holidays,', data?.world_days?.length, 'world_days,', data?.city_events?.length, 'city_events for locationId', weatherLocationId, 'start', start);
        setWeekEvents(unifyEvents(data));
      })
      .catch((err: any) => {
        console.warn('[ShiftEvents] fetch failed:', err?.status || err?.message || err);
        setWeekEvents([]);
      });
  }, [weatherLocationId, startDate]);

  // ===== Absences fetch =====
  useEffect(() => {
    const toISODate = (d: Date) => d.toISOString().slice(0, 10);
    const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
    const overlapsDay = (a: Absence, dayISO: string) => {
      const ds = new Date(`${dayISO}T00:00:00`).getTime();
      const de = new Date(`${dayISO}T23:59:59`).getTime();
      const as2 = dateUtil.createLocalDate(a.start_datetime).getTime();
      const ae = dateUtil.createLocalDate(a.end_datetime).getTime();
      return as2 <= de && ae > ds;
    };
    const load = async () => {
      if (activeTab !== 'shifts' && activeTab !== 'absences') return;
      try {
        const monday = dateUtil.getMonday(startDate);
        const startISO = toISODate(monday);
        const endISO = toISODate(addDays(monday, 6));
        const res = await httpService.get('/api/absences/');
        const data: any = res.data;
        const allAbsences: Absence[] = (data?.results ?? data) as Absence[];
        setAllAbsencesForSidebar(allAbsences);
        const mondayTime = new Date(`${startISO}T00:00:00`).getTime();
        const sundayTime = new Date(`${endISO}T23:59:59`).getTime();
        const list = allAbsences.filter(a => {
          const statusUpper = (a.status || '').toUpperCase();
          if (statusUpper !== 'APPROVED' && statusUpper !== 'REQUESTED') return false;
          const as2 = dateUtil.createLocalDate(a.start_datetime).getTime();
          const ae = dateUtil.createLocalDate(a.end_datetime).getTime();
          return as2 <= sundayTime && ae >= mondayTime;
        });
        console.log('[WeeklyShifts] Loaded absences for pills:', { startISO, endISO, total: allAbsences.length, filtered: list.length, list });
        setWeekAbsences(list);
        const counts: Record<string, { red: number; orange: number }> = {};
        for (let i = 0; i < 7; i++) {
          const dISO = toISODate(addDays(monday, i));
          const red = new Set<number>();
          const orange = new Set<number>();
          list.forEach(a => {
            if (overlapsDay(a, dISO)) {
              const statusUpper = (a.status || '').toUpperCase();
              if (statusUpper === 'APPROVED') red.add(a.employee);
              else if (statusUpper === 'REQUESTED') orange.add(a.employee);
            }
          });
          red.forEach(empId => orange.delete(empId));
          counts[dISO] = { red: red.size, orange: orange.size };
        }
        console.log('[WeeklyShifts] Computed absence counts:', counts);
        setCountsByDate(counts);
      } catch (e) {
        console.error('[WeeklyShifts] Failed to load absences for pills:', e);
      }
    };
    load();
  }, [activeTab, startDate]);

  return {
    weatherData,
    weatherByLocation,
    uniqueLocations,
    weekEvents,
    weekAbsences,
    allAbsencesForSidebar,
    countsByDate,
  };
}
